import {
  POKER_MIN_BUYIN, POKER_MAX_BUYIN, BIG_BLIND,
} from "@ghostland/shared";
import { pokerStore, type PokerTableView } from "../items/pokerStore.js";
import { balanceStore } from "../items/balanceStore.js";
import { drawCard } from "../items/cardArt.js";
import { injectTheme } from "./theme.js";

/**
 * The Texas Hold'em HUD — a NON-occluding overlay that sits on top of the first-person 3D table
 * view (the felt, dealer, opponents and flying cards are rendered in 3D by world3d/cards3d). The
 * HUD shows: the pot + community strip (top), a compact opponents readout, the owner's own hole
 * cards + chips (bottom-left), the action controls / buy-in (bottom-centre) and the cash-out /
 * leave buttons (bottom-right). A gold-coin pop plays when the wallet is paid a cash-out.
 */

const STYLE_ID = "ghostland-pk-style";

const ACTION_LABEL: Record<string, string> = {
  post: "blind", call: "call", raise: "raise", check: "check", fold: "fold", allin: "ALL IN", won: "WON",
};
const PHASE_LABEL: Record<string, string> = {
  idle: "Waiting for players…", waiting: "Next hand starting…", preflop: "Pre-flop", flop: "Flop", turn: "Turn",
  river: "River", showdown: "Showdown", reset: "Hand over",
};

const CSS = `
#gl-pk-hud { position: fixed; inset: 0; z-index: 28; display: none; pointer-events: none;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text); }
#gl-pk-hud.open { display: block; }
#gl-pk-hud .panel { position: absolute; pointer-events: auto;
  background: rgba(8,6,10,.82); border: 2px solid #ffffff; border-radius: 8px;
  box-shadow: 0 0 0 2px rgba(0,0,0,.6), 0 6px 18px rgba(0,0,0,.6); image-rendering: pixelated; }

/* top centre: title + pot + community board */
#gl-pk-top { top: 12px; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 8px 16px; }
#gl-pk-top .line { display: flex; gap: 14px; align-items: baseline; }
#gl-pk-top h2 { margin: 0; font-size: 13px; letter-spacing: 2px; color: #fff; }
#gl-pk-phase { font-size: 12px; color: #b0b0b0; }
#gl-pk-pot { font-size: 14px; color: #ffe08a; }
#gl-pk-board { display: flex; gap: 6px; min-height: 92px; align-items: center; }
#gl-pk-board canvas { width: 66px; height: 92px; image-rendering: pixelated; }
@keyframes gl-pk-fade { from { opacity: 0; transform: translateY(-8px) scale(.9); } to { opacity: 1; transform: none; } }
#gl-pk-board canvas, #gl-pk-myhole canvas { animation: gl-pk-fade .25s ease-out both; }

/* opponents readout: a vertical strip on the right */
#gl-pk-opps { top: 86px; right: 12px; width: 180px; display: flex; flex-direction: column; gap: 4px; padding: 8px; }
#gl-pk-opps .opp { display: flex; flex-direction: column; gap: 1px; padding: 4px 6px; border-radius: 5px; background: rgba(255,255,255,.04); }
#gl-pk-opps .opp.active { background: rgba(196,181,253,.22); box-shadow: 0 0 0 1px rgba(196,181,253,.7) inset; }
#gl-pk-opps .opp.folded { opacity: .42; }
#gl-pk-opps .opp .nm { font-size: 11px; font-weight: 700; color: #fff; display: flex; justify-content: space-between; }
#gl-pk-opps .opp .nm .btn-d { background: #fff; color: #000; border-radius: 50%; width: 14px; height: 14px; font-size: 9px; text-align: center; line-height: 14px; }
#gl-pk-opps .opp .ln { font-size: 10px; color: #cfcfcf; display: flex; justify-content: space-between; }
#gl-pk-opps .opp .ln .bet { color: #ffe08a; }
#gl-pk-opps .opp .ln .act { color: #9ad29a; text-transform: uppercase; letter-spacing: 1px; }
#gl-pk-opps .title { font-size: 10px; letter-spacing: 1px; color: #888; margin-bottom: 2px; }

/* my hole cards + chips, bottom-left */
#gl-pk-me { bottom: 14px; left: 14px; display: flex; gap: 10px; align-items: flex-end; padding: 10px 12px; }
#gl-pk-myhole { display: flex; gap: 6px; min-height: 112px; }
#gl-pk-myhole canvas { width: 80px; height: 112px; image-rendering: pixelated; }
#gl-pk-me .meinfo { display: flex; flex-direction: column; gap: 2px; }
#gl-pk-me .nm { font-size: 12px; font-weight: 700; color: #ffe08a; }
#gl-pk-me .chips { font-size: 13px; color: #9ad29a; font-weight: 700; }
#gl-pk-me .bet { font-size: 11px; color: #ffe08a; }
#gl-pk-me .st { font-size: 10px; color: #b0b0b0; text-transform: uppercase; letter-spacing: 1px; }

/* controls, bottom-centre */
#gl-pk-controls { bottom: 14px; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; gap: 8px; align-items: center; padding: 10px 14px; min-width: 280px; }
#gl-pk-controls .row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; justify-content: center; }
#gl-pk-controls .hint { font-size: 12px; color: #b0b0b0; }
#gl-pk-controls .countdown { color: #ffe08a; }
#gl-pk-controls .title { font-size: 12px; letter-spacing: 1px; color: #fff; }
#gl-pk-controls .sub { font-size: 11px; color: #b0b0b0; }
.gl-pk-btn { font: inherit; font-size: 13px; padding: 8px 16px; cursor: pointer;
  background: #161616; color: #fff; border: 2px solid #fff; border-radius: 6px; box-shadow: 0 2px 0 rgba(0,0,0,.6); }
.gl-pk-btn:hover { background: #232323; }
.gl-pk-btn.primary { background: #fff; color: #000; }
.gl-pk-btn:disabled { opacity: .4; cursor: default; }
.gl-pk-btn.danger { border-color: var(--gl-error); }
.gl-pk-range { width: 200px; }
.gl-pk-amt { font-size: 13px; color: #ffe08a; min-width: 64px; text-align: center; }

/* cash out / leave, bottom-right */
#gl-pk-foot { bottom: 14px; right: 12px; display: flex; flex-direction: column; gap: 8px; padding: 8px; }

@keyframes gl-pk-coin { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-80px) scale(1.4); opacity: 0; } }
.gl-pk-coin { position: fixed; font-size: 22px; pointer-events: none; animation: gl-pk-coin 1s ease-out forwards; z-index: 40; }
`;

/** Intents the HUD can fire. Registered by World3D. */
export interface PokerActions {
  buyIn?: (amount: number) => void;
  act?: (action: "fold" | "check" | "call" | "raise", amount?: number) => void;
  withdraw?: () => void;
  leave?: () => void;
}

let actions: PokerActions = {};
export function setPokerActions(a: PokerActions): void {
  actions = a;
}

let hud: HTMLDivElement | null = null;
let phaseEl: HTMLSpanElement | null = null;
let potEl: HTMLSpanElement | null = null;
let boardEl: HTMLDivElement | null = null;
let oppsEl: HTMLDivElement | null = null;
let meEl: HTMLDivElement | null = null;
let myHoleEl: HTMLDivElement | null = null;
let controlsEl: HTMLDivElement | null = null;
let current: PokerTableView | null = null;
let lastControlsSig = "";
let lastBalance = balanceStore.get();

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function ensureHud(): HTMLDivElement {
  if (hud) return hud;
  ensureStyle();
  hud = document.createElement("div");
  hud.id = "gl-pk-hud";
  hud.innerHTML = `
    <div class="panel" id="gl-pk-top">
      <div class="line"><h2>TEXAS HOLD'EM</h2><span id="gl-pk-pot"></span><span id="gl-pk-phase"></span></div>
      <div id="gl-pk-board"></div>
    </div>
    <div class="panel" id="gl-pk-opps"></div>
    <div class="panel" id="gl-pk-me">
      <div id="gl-pk-myhole"></div>
      <div class="meinfo"><span class="nm"></span><span class="chips"></span><span class="bet"></span><span class="st"></span></div>
    </div>
    <div class="panel" id="gl-pk-controls"></div>
    <div class="panel" id="gl-pk-foot">
      <button class="gl-pk-btn" id="gl-pk-cashout" type="button">Cash Out</button>
      <button class="gl-pk-btn danger" id="gl-pk-leave" type="button">Leave</button>
    </div>`;
  phaseEl = hud.querySelector<HTMLSpanElement>("#gl-pk-phase")!;
  potEl = hud.querySelector<HTMLSpanElement>("#gl-pk-pot")!;
  boardEl = hud.querySelector<HTMLDivElement>("#gl-pk-board")!;
  oppsEl = hud.querySelector<HTMLDivElement>("#gl-pk-opps")!;
  meEl = hud.querySelector<HTMLDivElement>("#gl-pk-me")!;
  myHoleEl = hud.querySelector<HTMLDivElement>("#gl-pk-myhole")!;
  controlsEl = hud.querySelector<HTMLDivElement>("#gl-pk-controls")!;
  hud.querySelector<HTMLButtonElement>("#gl-pk-cashout")!.addEventListener("click", () => actions.withdraw?.());
  hud.querySelector<HTMLButtonElement>("#gl-pk-leave")!.addEventListener("click", () => actions.leave?.());
  document.body.appendChild(hud);
  pokerStore.subscribe(render);
  balanceStore.subscribe(onBalance);
  window.setInterval(tickCountdown, 250);
  return hud;
}

function onBalance(balance: number): void {
  if (hud?.classList.contains("open") && balance > lastBalance) coinPop();
  lastBalance = balance;
}

function coinPop(): void {
  const foot = hud?.querySelector<HTMLButtonElement>("#gl-pk-cashout");
  if (!foot) return;
  const r = foot.getBoundingClientRect();
  for (let i = 0; i < 9; i++) {
    const coin = document.createElement("div");
    coin.className = "gl-pk-coin";
    coin.textContent = "🪙";
    coin.style.left = `${r.left + 20 + (Math.random() * 50 - 25)}px`;
    coin.style.top = `${r.top - 6}px`;
    coin.style.animationDelay = `${i * 0.05}s`;
    document.body.appendChild(coin);
    window.setTimeout(() => coin.remove(), 1200);
  }
}

function secondsLeft(table: PokerTableView): number {
  if (!table.timerEndsAt) return 0;
  return Math.max(0, Math.ceil((table.timerEndsAt - Date.now()) / 1000));
}
function tickCountdown(): void {
  if (!current || !controlsEl) return;
  const el = controlsEl.querySelector<HTMLSpanElement>(".countdown");
  if (el) el.textContent = `${secondsLeft(current)}s`;
}

function cardsRow(el: HTMLElement, codes: string[]): void {
  const sig = codes.join(",") || "-";
  if (el.dataset.sig === sig) return;
  el.dataset.sig = sig;
  el.innerHTML = "";
  for (const code of codes) el.appendChild(drawCard(code));
}

function render(table: PokerTableView): void {
  if (!hud) return;
  current = table;
  if (phaseEl) phaseEl.textContent = PHASE_LABEL[table.phase] ?? table.phase;
  if (potEl) potEl.textContent = table.pot > 0 ? `POT $${table.pot.toLocaleString()}` : "";
  if (boardEl) cardsRow(boardEl, table.community ? table.community.split(" ").filter(Boolean) : []);

  renderOpponents(table);
  renderMe(table);
  renderControls(table);
}

function renderOpponents(table: PokerTableView): void {
  if (!oppsEl) return;
  const opps = table.seats.filter((s) => s.occupied && s.index !== table.mySeat);
  oppsEl.style.display = opps.length ? "flex" : "none";
  oppsEl.innerHTML = `<div class="title">TABLE</div>`;
  for (const s of opps) {
    const div = document.createElement("div");
    div.className = `opp${table.activeSeat === s.index ? " active" : ""}${s.status === "folded" ? " folded" : ""}`;
    const dealer = table.button === s.index ? `<span class="btn-d">D</span>` : "";
    const act = ACTION_LABEL[s.lastAction] ?? "";
    const revealed = s.revealed ? ` · ${s.revealed}` : "";
    div.innerHTML = `
      <div class="nm"><span>${escapeHtml(s.name)}${revealed}</span>${dealer}</div>
      <div class="ln"><span>$${s.chips.toLocaleString()}</span>${s.bet > 0 ? `<span class="bet">bet $${s.bet.toLocaleString()}</span>` : `<span class="act">${act}</span>`}</div>`;
    oppsEl.appendChild(div);
  }
}

function renderMe(table: PokerTableView): void {
  if (!meEl || !myHoleEl) return;
  const me = table.seats.find((s) => s.index === table.mySeat);
  if (!me) { meEl.style.display = "none"; return; }
  meEl.style.display = "flex";
  // Show my own (private) hole cards, or my revealed cards at showdown.
  const cards = (me.revealed || table.myHole || "").split(" ").filter(Boolean);
  cardsRow(myHoleEl, cards);
  meEl.querySelector<HTMLSpanElement>(".nm")!.textContent = `${me.name} (you)`;
  meEl.querySelector<HTMLSpanElement>(".chips")!.textContent = `$${me.chips.toLocaleString()} chips`;
  meEl.querySelector<HTMLSpanElement>(".bet")!.textContent = me.bet > 0 ? `in pot: $${me.bet.toLocaleString()}` : "";
  const st = meEl.querySelector<HTMLSpanElement>(".st")!;
  st.textContent = me.status === "allin" ? "ALL IN" : me.status === "folded" ? "folded" : me.status === "won" ? "WINNER" : "";
}

function renderControls(table: PokerTableView): void {
  if (!controlsEl) return;
  const me = table.seats.find((s) => s.index === table.mySeat);
  const wallet = balanceStore.get();
  const needsBuyIn = table.mySeat >= 0 && (!me || me.chips === 0);
  const myTurn = table.mySeat >= 0 && table.activeSeat === table.mySeat && me?.status === "active";
  const toCall = me ? Math.max(0, table.currentBet - me.bet) : 0;
  const callAmt = me ? Math.min(toCall, me.chips) : 0;
  const sig = `${needsBuyIn ? `buy:${wallet}` : ""}|${table.phase}|${table.activeSeat}|${table.mySeat}|${me?.chips ?? 0}|${me?.bet ?? 0}|${table.currentBet}|${table.minRaise}|${myTurn}`;
  if (sig === lastControlsSig) return;
  lastControlsSig = sig;
  controlsEl.innerHTML = "";
  if (table.mySeat < 0) { controlsEl.style.display = "none"; return; }
  controlsEl.style.display = "flex";

  if (needsBuyIn) { buildBuyIn(wallet); return; }

  if (!myTurn) {
    const hint = document.createElement("div");
    hint.className = "hint";
    if (table.phase === "idle") hint.textContent = "Waiting for another player to sit in…";
    else if (table.phase === "waiting") hint.innerHTML = `Next hand in <span class="countdown"></span> — buy in / get ready`;
    else if (table.phase === "reset" || table.phase === "showdown") hint.textContent = "Hand over — next one soon.";
    else if (me?.status === "folded") hint.textContent = "You folded — next hand soon.";
    else if (me?.status === "allin") hint.textContent = "You're all in.";
    else hint.innerHTML = `Waiting for your turn… <span class="countdown"></span>`;
    controlsEl.appendChild(hint);
    return;
  }

  const row = document.createElement("div");
  row.className = "row";
  row.appendChild(btn("Fold", () => actions.act?.("fold")));
  if (toCall === 0) row.appendChild(btn("Check", () => actions.act?.("check")));
  else row.appendChild(btn(`Call $${callAmt.toLocaleString()}`, () => actions.act?.("call")));
  controlsEl.appendChild(row);

  if (me && me.chips > callAmt) {
    const allInTo = me.bet + me.chips;
    const minTo = Math.min(allInTo, table.currentBet + table.minRaise);
    let raiseTo = minTo;
    const rrow = document.createElement("div");
    rrow.className = "row";
    rrow.innerHTML = `<input class="gl-pk-range" type="range" min="${minTo}" max="${allInTo}" step="${BIG_BLIND}" value="${minTo}"><span class="gl-pk-amt">$${minTo.toLocaleString()}</span>`;
    controlsEl.appendChild(rrow);
    const range = rrow.querySelector<HTMLInputElement>("input")!;
    const amt = rrow.querySelector<HTMLSpanElement>(".gl-pk-amt")!;
    range.addEventListener("input", () => { raiseTo = Number(range.value); amt.textContent = `$${raiseTo.toLocaleString()}`; });
    const rrow2 = document.createElement("div");
    rrow2.className = "row";
    const raiseBtn = btn(toCall === 0 ? "Bet" : "Raise", () => actions.act?.("raise", raiseTo));
    raiseBtn.classList.add("primary");
    rrow2.appendChild(raiseBtn);
    rrow2.appendChild(btn("All In", () => actions.act?.("raise", allInTo)));
    controlsEl.appendChild(rrow2);
  }

  const cd = document.createElement("div");
  cd.className = "hint";
  cd.innerHTML = `Your turn — <span class="countdown">${secondsLeft(table)}s</span>`;
  controlsEl.appendChild(cd);
}

function buildBuyIn(wallet: number): void {
  if (!controlsEl) return;
  const maxAffordable = Math.min(POKER_MAX_BUYIN, wallet);
  if (wallet < POKER_MIN_BUYIN) {
    controlsEl.innerHTML = `<div class="title">BUY IN</div><div class="sub">Need $${POKER_MIN_BUYIN.toLocaleString()}+ cash to sit in. Wallet: $${wallet.toLocaleString()}.</div>`;
    return;
  }
  let amount = POKER_MIN_BUYIN;
  controlsEl.innerHTML = `
    <div class="title">BUY IN — blinds $${BIG_BLIND / 2}/$${BIG_BLIND}</div>
    <div class="row">
      <input class="gl-pk-range" type="range" min="${POKER_MIN_BUYIN}" max="${maxAffordable}" step="${BIG_BLIND}" value="${amount}">
      <span class="gl-pk-amt">$${amount.toLocaleString()}</span>
      <button class="gl-pk-btn primary" type="button">Buy In</button>
    </div>`;
  const range = controlsEl.querySelector<HTMLInputElement>("input")!;
  const amt = controlsEl.querySelector<HTMLSpanElement>(".gl-pk-amt")!;
  range.addEventListener("input", () => { amount = Number(range.value); amt.textContent = `$${amount.toLocaleString()}`; });
  controlsEl.querySelector<HTMLButtonElement>("button")!.addEventListener("click", () => actions.buyIn?.(amount));
}

function btn(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "gl-pk-btn";
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}

export function openPokerTable(): void {
  lastControlsSig = "";
  lastBalance = balanceStore.get();
  ensureHud().classList.add("open");
}
export function closePokerTable(): void {
  hud?.classList.remove("open");
}
export function isPokerTableOpen(): boolean {
  return !!hud?.classList.contains("open");
}
